import {Injectable} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {RouterStateSnapshot, TitleStrategy} from '@angular/router';
import {TranslocoService} from '@ngneat/transloco';

/**
 * Custom {@link TitleStrategy} implementation that integrates Angular Router
 * with Transloco to dynamically set translated page titles.
 *
 * This strategy reads the `title` property defined in the route configuration,
 * treats it as a translation key, translates it using Transloco (in the `routes` scope),
 * and updates the browser document title accordingly.
 *
 * The title is also automatically updated whenever the active Transloco language changes,
 * ensuring the page title always matches the selected language.
 *
 * @example
 * ```ts
 * {
 *   path: 'dashboard',
 *   title: 'pages.dashboard.title',
 *   component: DashboardComponent
 * }
 * ```
 */
@Injectable()
export class TranslocoTitleStrategy extends TitleStrategy {
  /**
   * Creates an instance of {@link TranslocoTitleStrategy}.
   *
   * @param title Angular service used to set the document title.
   * @param transloco Transloco service used to translate title keys.
   */
  constructor(
    private readonly title: Title,
    private readonly transloco: TranslocoService,
  ) {
    super();
    this.transloco.langChanges$.subscribe(() => {
      const snapshot = this.lastRouterState;
      if (snapshot) {
        this.updateTitle(snapshot);
      }
    });
  }

  private lastRouterState: RouterStateSnapshot | null = null;

  /**
   * Updates the browser title based on the current router state.
   *
   * It retrieves the route title using {@link TitleStrategy.buildTitle},
   * translates it using Transloco, and applies it to the document.
   *
   * If no title is defined in the route configuration, the method exits
   * without performing any action.
   *
   * @param routerState The current router state snapshot.
   */
  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.lastRouterState = snapshot;
    const titleKey = this.buildTitle(snapshot);
    if (titleKey) {
      this.transloco.selectTranslate(titleKey, {}, 'routes').subscribe((translated) => {
        this.title.setTitle(translated);
      });
    }
  }
}
